/**
 * Feedback Service
 * Service quản lý phản hồi trong Admin Portal
 */

import axios from 'axios';
import { AuthService } from './authService';

export interface Feedback {
  id: number;
  userId: number;
  username: string;
  fullName?: string;
  subject: string;
  content: string;
  type: string;
  portal: string;
  status: string;
  response?: string;
  responseBy?: string;
  responseAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface FeedbackStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  technical: number;
  financial: number;
  academic: number;
  other: number;
  fromStudents: number;
  fromTeachers: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
  };
}

class FeedbackService {
  private getHeaders() {
    return AuthService.getAuthHeader();
  }

  /**
   * [Admin] Lấy danh sách phản hồi
   */
  async getAdminFeedbacks(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    portal?: string;
  }): Promise<ApiResponse<Feedback[]>> {
    try {
      const response = await axios.get<ApiResponse<Feedback[]>>('/api/v1/feedback/admin', {
        headers: this.getHeaders(),
        params
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể tải danh sách phản hồi');
    }
  }

  /**
   * [Admin] Lấy thống kê phản hồi
   */
  async getStats(): Promise<ApiResponse<FeedbackStats>> {
    try {
      const response = await axios.get<ApiResponse<FeedbackStats>>('/api/v1/feedback/admin/stats', {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể tải thống kê');
    }
  }

  /**
   * [Admin] Trả lời phản hồi
   */
  async respondToFeedback(id: number, data: { response: string; status?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await axios.put<ApiResponse<any>>(`/api/v1/feedback/admin/${id}/respond`, data, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể gửi trả lời');
    }
  }

  /**
   * [Admin] Cập nhật trạng thái phản hồi
   */
  async updateStatus(id: number, status: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.put<ApiResponse<any>>(`/api/v1/feedback/admin/${id}/status`, { status }, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể cập nhật trạng thái');
    }
  }

  /**
   * [Admin] Xóa phản hồi
   */
  async deleteFeedback(id: number): Promise<ApiResponse<any>> {
    try {
      const response = await axios.delete<ApiResponse<any>>(`/api/v1/feedback/admin/${id}`, {
        headers: this.getHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Không thể xóa phản hồi');
    }
  }
}

export default new FeedbackService();

